import { useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import { useNotify } from "../../components/notifications";
import { AppDialog } from "../../components/AppDialog";
import { QuickAddDialog } from "./QuickAddDialog";
import { CountryDialog } from "./CountryDialog";
import { OpenProspectDialog } from "./OpenProspectDialog";
import { AiCombo } from "../../components/AiCombo";
import { ModuleCrudDialog } from "./ModuleCrudDialog";
import { MODULE_CRUD, PENDING_LABELS_KEY, type CrudField } from "./moduleCrud";

interface Option {
  id: string;
  name: string;
}

type QuickAddSpec = NonNullable<CrudField["quickAdd"]>;

interface Props {
  /** POST endpoint that creates the client (/api/v1/customers). */
  createUrl: string;
  /** The parent Client lookup's optionsUrl — re-read after create to pick up the fresh
   *  {id, name} and select it. */
  optionsUrl: string;
  onClose: () => void;
  onAdded: (options: Option[], selectedId: string) => void;
}

type FieldType = "text" | "select" | "date" | "number";
interface FieldDef {
  key: string;        // = the create-API payload key (CustomerInput, camelCase)
  label: string;
  type: FieldType;
  required?: boolean;
  /** Read-only display (e.g. Client Code = "Autogenerate"); never sent. */
  disabled?: boolean;
  /** For a select: the options endpoint (loaded on open). */
  src?: string;
  /** For a select: static options (Preferred Contact Method enum). */
  options?: Option[];
  /** A welded "+" quick-add popup for the lookup (Category) — opens QuickAddDialog. */
  quickAdd?: QuickAddSpec;
  /** The Country "+" opens the full Country master popup (CountryDialog). */
  quickAddCountry?: boolean;
  /** The Source Lead "+" opens the full "Open Prospect" form (OpenProspectDialog). */
  quickAddProspect?: boolean;
}
// null = an empty grid cell so the 2-column rows line up like the legacy form.
type Cell = FieldDef | null;

const PCM: Option[] = [
  { id: "0", name: "Any" }, { id: "1", name: "Email" }, { id: "2", name: "Phone" },
  { id: "3", name: "Fax" }, { id: "4", name: "Mail" },
];

// The legacy Customer DetailView (image 978), section by section. Each section is a flat list of
// cells rendered in a 2-column, row-major grid; the order interleaves the legacy left / right
// columns so each row reads the same as the old form. Field `key` = the CustomerInput payload key.
// This mirrors the crm-unfollowed-clients config field set (the authoritative G6 Customer form).
const SECTIONS: { title: string; cells: Cell[] }[] = [
  {
    title: "Summary",
    cells: [
      { key: "client", label: "Client", type: "text", required: true },
      { key: "clientNumber", label: "Client Number", type: "text" },
      // Client Code is minted server-side (race-safe) — shown disabled, never sent.
      { key: "clientCode", label: "Client Code", type: "text", disabled: true },
      { key: "phone", label: "Phone", type: "text" },
      { key: "account", label: "Account", type: "select", src: "/api/v1/customers/accounts" },
      { key: "website", label: "WebSite", type: "text" },
      { key: "category", label: "Category", type: "select", src: "/api/v1/customers/categories",
        quickAdd: { createUrl: "/api/v1/clients/lookups/categories", title: "Category", icon: "ti-category",
          fields: [{ key: "category", label: "Category", type: "text", required: true, requiredMessage: "Category is required." },
                   { key: "description", label: "Description", type: "textarea" }] } },
      { key: "industry", label: "Industry", type: "select", src: "/api/v1/customers/industries" },
      { key: "fax", label: "Fax", type: "text" },
      { key: "primaryContact", label: "Primary Contact", type: "select", src: "/api/v1/customers/contacts" },
      { key: "email", label: "Email", type: "text" },
      { key: "licenseNumber", label: "License Number", type: "text" },
    ],
  },
  {
    title: "Address",
    cells: [
      { key: "address1", label: "Address 1", type: "text" },
      { key: "address2", label: "Address 2", type: "text" },
      { key: "country", label: "Country", type: "select", src: "/api/v1/customers/countries", quickAddCountry: true },
      { key: "postCode", label: "Post Code", type: "text" },
      { key: "state", label: "State", type: "select", src: "/api/v1/customers/states" },
      { key: "zip", label: "Zip", type: "text" },
      { key: "city", label: "City", type: "select", src: "/api/v1/customers/cities" },
      { key: "sicCode", label: "SIC Code", type: "text" },
    ],
  },
  {
    title: "Account Management",
    cells: [
      { key: "owner", label: "Owner", type: "select", src: "/api/v1/customers/owners" },
      { key: "sourceLead", label: "Source Lead", type: "select", src: "/api/v1/customers/source-leads", quickAddProspect: true },
      { key: "creditLimit", label: "Credit Limit", type: "text" },
      { key: "preferredContactMethod", label: "Preferred Contact Method", type: "select", options: PCM },
      { key: "contractDate", label: "Contract Date", type: "date" },
      { key: "startDate", label: "Start Date", type: "date" },
      { key: "maintStartDate", label: "Maintenance Start Date", type: "date" },
      { key: "maintEndDate", label: "Maintenance End Date", type: "date" },
      { key: "acceptanceDate", label: "Acceptance Date", type: "date" },
      { key: "reminderDate", label: "Reminder Date", type: "date" },
    ],
  },
];

const ALL_FIELDS: FieldDef[] = SECTIONS.flatMap((s) => s.cells.filter((c): c is FieldDef => c != null));

// The legacy Customer DetailView's 5 bottom tabs (image 982). On a BRAND-NEW client the rows are
// BUFFERED in the dialog (owner decision 2026-10-26) — New/Edit/Delete add/edit/remove rows via the
// SAME child forms the Unfollowed Clients tabs use (ModuleCrudDialog + the crm-customer-* configs,
// captured through onCapture), and on OK they are persisted under the newly-created client. Each tab
// declares its child config, its display columns, a payload→row mapper, and its persist endpoint.
type Row = Record<string, unknown>;
const txt = (p: Row, k: string) => String(p[k] ?? "");
const lbl = (p: Row, k: string) => {
  const L = (p[PENDING_LABELS_KEY] ?? {}) as Record<string, string>;
  const base = k.replace(/Id$/, "");
  return L[`${base}Name`] ?? L[`${base}Display`] ?? txt(p, k);
};
const yn = (p: Row, k: string) => (p[k] ? "✓" : "");
const clean = (p: Row): Row => { const rest = { ...p }; delete rest[PENDING_LABELS_KEY]; return rest; };

interface TabDef {
  key: string;
  label: string;
  childKey: string;                 // the crm-customer-* MODULE_CRUD config
  fixed?: Record<string, unknown>;  // stamped into every New/Edit payload (Invoicing: type "1")
  cols: string[];
  row: (p: Row) => string[];        // cell values aligned to `cols`
  persist: (newId: string, p: Row) => Promise<unknown>;
}
const TAB_DEFS: TabDef[] = [
  { key: "contacts", label: "Contacts", childKey: "crm-customer-contact",
    cols: ["ReportContact", "ReportMail", "ReportMailCC", "ReportMailBCC", "ReportPhone", "Comment"],
    row: (p) => [`${txt(p, "firstName")} ${txt(p, "lastName")}`.trim(), txt(p, "email"), txt(p, "emailCc"), txt(p, "emailBcc"), txt(p, "officePhone"), txt(p, "comment")],
    persist: (id, p) => api.post(`/api/v1/clients/${id}/contacts`, clean(p)) },
  { key: "invoicing", label: "Invoicing Contact", childKey: "crm-customer-invoicing", fixed: { type: "1" },
    cols: ["InvoiceContact", "InvoiceMail", "InvoiceMailCC", "InvoiceMailBCC", "InvoicePhone", "Comment"],
    row: (p) => [`${txt(p, "firstName")} ${txt(p, "lastName")}`.trim(), txt(p, "email"), txt(p, "emailCc"), txt(p, "emailBcc"), txt(p, "officePhone"), txt(p, "comment")],
    persist: (id, p) => api.post(`/api/v1/clients/${id}/contacts`, { ...clean(p), type: "1" }) },
  { key: "projects", label: "Projects", childKey: "crm-customer-project",
    cols: ["ProjectId", "ProjectName", "ProjectLocation", "RetiredDate"],
    row: (p) => [txt(p, "projectId"), txt(p, "projectName"), txt(p, "projectLocation"), txt(p, "retiredDate")],
    persist: (id, p) => api.post(`/api/v1/clients/${id}/projects`, clean(p)) },
  { key: "collectors", label: "Collectors", childKey: "crm-customer-collector",
    cols: ["FullName", "FirstName", "LastName", "ContactPhone", "Comment", "IsDefault", "IsFieldUser", "DontShow"],
    row: (p) => [`${txt(p, "firstName")} ${txt(p, "lastName")}`.trim(), txt(p, "firstName"), txt(p, "lastName"), txt(p, "contactPhone"), txt(p, "comment"), yn(p, "isDefault"), yn(p, "isFieldUser"), yn(p, "dontShow")],
    persist: (id, p) => api.post(`/api/v1/clients/${id}/collectors`, clean(p)) },
  { key: "notes", label: "Notes", childKey: "crm-customer-note",
    cols: ["Title", "Note", "Category", "FollowUpDate"],
    row: (p) => [txt(p, "title"), txt(p, "text"), lbl(p, "categoryId"), txt(p, "followUpDate")],
    persist: (id, p) => api.post(`/api/v1/crm-notes`, { ...clean(p), ownerId: id }) },
];

/**
 * The legacy Customer DetailView, opened from the "+" on a Client grid-lookup (owner request
 * 2026-10-26, images 977/978 — the "Notes in Clients" Client field). Creates a dbo.Customer via
 * POST /api/v1/customers (the SAME backend the Unfollowed Clients form uses — standing rule 8),
 * then re-reads the parent Client lookup's options and selects the new row. Sections / fields /
 * lookups mirror the crm-unfollowed-clients Customer form (SUMMARY / ADDRESS / ACCOUNT
 * MANAGEMENT). The 5 child tabs (Contacts / Invoicing / Projects / Collectors / Notes) are empty
 * on a brand-new client and are managed from the Unfollowed Clients detail, so they are not part
 * of this create dialog. Category + Country carry the welded "+" (QuickAddDialog / CountryDialog).
 */
export function ClientDialog({ createUrl, optionsUrl, onClose, onAdded }: Props) {
  const notify = useNotify();
  const [values, setValues] = useState<Record<string, string>>(() => ({ preferredContactMethod: "0", creditLimit: "0.00" }));
  const [opts, setOpts] = useState<Record<string, Option[]>>({});
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [qaField, setQaField] = useState<FieldDef | null>(null);
  const [countryField, setCountryField] = useState<FieldDef | null>(null);
  const [prospectField, setProspectField] = useState<FieldDef | null>(null);
  const [tab, setTab] = useState<string>("contacts");
  // Buffered child rows per tab + the open child form + the selected row per tab.
  const [buffered, setBuffered] = useState<Record<string, Row[]>>({});
  const [childDlg, setChildDlg] = useState<{ tab: string; editIdx: number | null } | null>(null);
  const [sel, setSel] = useState<Record<string, Set<number>>>({});
  const tabDef = TAB_DEFS.find((t) => t.key === tab)!;
  const tabRows = buffered[tab] ?? [];
  const tabSel = sel[tab] ?? new Set<number>();

  // Load every lookup's options once on open (distinct endpoints, in parallel).
  useEffect(() => {
    const urls = Array.from(new Set(ALL_FIELDS.map((f) => f.src).filter((u): u is string => !!u)));
    let alive = true;
    void Promise.all(
      urls.map(async (u) => {
        try {
          const { data } = await api.get<{ data?: Option[] }>(u, { silentLoading: true } as never);
          return [u, data?.data ?? []] as const;
        } catch {
          return [u, [] as Option[]] as const;
        }
      }),
    ).then((pairs) => {
      if (alive) setOpts(Object.fromEntries(pairs));
    });
    return () => {
      alive = false;
    };
  }, []);

  const missingRequired = useMemo(
    () => ALL_FIELDS.some((f) => f.required && !values[f.key]?.trim()),
    [values],
  );

  const set = (k: string, v: string) => setValues((p) => ({ ...p, [k]: v }));
  const toggleRow = (t: string, i: number) =>
    setSel((s) => { const cur = new Set(s[t] ?? []); if (cur.has(i)) cur.delete(i); else cur.add(i); return { ...s, [t]: cur }; });
  const toggleAll = (t: string, n: number) =>
    setSel((s) => { const cur = s[t] ?? new Set<number>(); const all = n > 0 && cur.size === n; return { ...s, [t]: all ? new Set<number>() : new Set(Array.from({ length: n }, (_, i) => i)) }; });
  const removeChecked = (t: string) => {
    const cur = sel[t] ?? new Set<number>();
    setBuffered((b) => ({ ...b, [t]: (b[t] ?? []).filter((_, i) => !cur.has(i)) }));
    setSel((s) => ({ ...s, [t]: new Set<number>() }));
  };

  // After a "+" popup creates a lookup value, replace that field's options with the fresh list and
  // select the new row (the popups re-read the field's own `src`, so IDs match).
  const applyAdded = (field: FieldDef, options: Option[], selectedId: string) => {
    if (field.src) setOpts((p) => ({ ...p, [field.src!]: options }));
    if (selectedId) set(field.key, selectedId);
  };

  const submit = async () => {
    setTried(true);
    if (missingRequired) return;
    setSaving(true);
    try {
      // Payload keys == field keys == CustomerInput keys. Empty strings dropped; the disabled
      // Client Code (autogen) is never sent.
      const payload: Record<string, string> = {};
      for (const f of ALL_FIELDS) {
        if (f.disabled) continue;
        const v = (values[f.key] ?? "").trim();
        if (v) payload[f.key] = v;
      }
      const created = await api.post<{ data?: { id?: string } }>(createUrl, payload);
      const newId = created.data?.data?.id ?? "";
      // Persist the buffered child rows under the new client (best-effort — the client is already
      // created, so a child failure is surfaced but does not roll it back).
      if (newId) {
        let childErr = false;
        for (const def of TAB_DEFS) {
          for (const r of buffered[def.key] ?? []) {
            try { await def.persist(newId, r); }
            catch { childErr = true; }
          }
        }
        if (childErr) notify.error("The client was created, but one or more tab rows could not be saved.");
      }
      // Re-read the parent's option list to resolve the new row — but ONLY when an optionsUrl was
      // given. A caller that just needs the new id (e.g. the Contact popup's Client "+") passes "",
      // and GET "" would throw and skip onAdded entirely (the client would be created but the "+"
      // appear to do nothing).
      let fresh: Option[] = [];
      if (optionsUrl) {
        const { data } = await api.get<{ data?: Option[] }>(optionsUrl, { silentLoading: true } as never);
        fresh = data?.data ?? [];
      }
      // Prefer the server-returned id; fall back to matching the typed Client name.
      const selected =
        (newId && fresh.find((o) => o.id.toLowerCase() === newId.toLowerCase())?.id) ||
        fresh.find((o) => o.name === values.client?.trim())?.id ||
        newId;
      notify.success("Saved successfully.");
      onAdded(fresh, selected);
      onClose();
    } catch {
      /* the shared axios error dialog already surfaced it */
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppDialog
      open
      title="Client"
      icon="ti-building"
      width={900}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ca-btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="ca-btn ca-btn-solid" onClick={submit} disabled={saving}>
            OK
          </button>
        </>
      }
    >
      <div className="op-form">
        {SECTIONS.map((sec) => (
          <div className="op-section" key={sec.title}>
            <div className="op-section-h">{sec.title}</div>
            <div className="op-grid">
              {sec.cells.map((cell, i) =>
                cell == null ? (
                  <div key={`sp-${sec.title}-${i}`} />
                ) : (
                  <div className="op-field" key={cell.key}>
                    <label className="op-label" htmlFor={`cl-${cell.key}`}>
                      {cell.label}
                      {cell.required ? <> :<span className="req">*</span></> : ":"}
                    </label>
                    {cell.type === "select" ? (
                      cell.quickAdd || cell.quickAddCountry || cell.quickAddProspect ? (
                        // Welded "+" — the shared <AiCombo> renders the "New" button + dropdown caret
                        // INSIDE one bordered control, not a separate box.
                        <AiCombo
                          value={values[cell.key] || null}
                          options={(cell.options ?? opts[cell.src ?? ""] ?? []).map((o) => ({ value: o.id, label: o.name }))}
                          onChange={(v) => set(cell.key, v ?? "")}
                          onNew={() =>
                            cell.quickAddProspect
                              ? setProspectField(cell)
                              : cell.quickAddCountry
                                ? setCountryField(cell)
                                : setQaField(cell)
                          }
                          newTitle={`New ${cell.label}`}
                          placeholder="— None —"
                          naLabel="— None —"
                          className={tried && cell.required && !values[cell.key]?.trim() ? "required-empty" : undefined}
                          compact
                        />
                      ) : (
                        <select
                          id={`cl-${cell.key}`}
                          className={`op-input${tried && cell.required && !values[cell.key]?.trim() ? " required-empty" : ""}`}
                          value={values[cell.key] ?? ""}
                          onChange={(e) => set(cell.key, e.target.value)}
                        >
                          <option value="">— None —</option>
                          {(cell.options ?? opts[cell.src ?? ""] ?? []).map((o) => (
                            <option key={o.id} value={o.id}>
                              {o.name}
                            </option>
                          ))}
                        </select>
                      )
                    ) : (
                      <input
                        id={`cl-${cell.key}`}
                        className={`op-input${tried && cell.required && !values[cell.key]?.trim() ? " required-empty" : ""}`}
                        type={cell.type === "date" ? "date" : cell.type === "number" ? "number" : "text"}
                        value={values[cell.key] ?? ""}
                        disabled={cell.disabled}
                        placeholder={cell.disabled ? "Autogenerate" : undefined}
                        onChange={(e) => set(cell.key, e.target.value)}
                      />
                    )}
                  </div>
                ),
              )}
            </div>
          </div>
        ))}

        {/* The 5 legacy child tabs (image 982) — FUNCTIONAL (owner request 2026-10-26): New/Edit/Delete
            buffer rows via the SAME child forms the Unfollowed Clients tabs use (ModuleCrudDialog + the
            crm-customer-* configs, captured through onCapture). The rows are persisted under the client
            when this dialog's OK creates it. */}
        <div className="op-childwrap">
          <div className="op-tabs">
            {TAB_DEFS.map((t) => (
              <button key={t.key} type="button" className={`op-tab${tab === t.key ? " active" : ""}`}
                      onClick={() => setTab(t.key)}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="op-childbar">
            <button type="button" className="op-cb" onClick={() => setChildDlg({ tab, editIdx: null })}>
              <i className="ti ti-plus" aria-hidden="true" /> New
            </button>
            <button type="button" className="op-cb" disabled={tabSel.size !== 1}
                    onClick={() => { const i = [...tabSel][0]; if (i != null) setChildDlg({ tab, editIdx: i }); }}>Edit</button>
            <button type="button" className="op-cb" disabled={tabSel.size === 0}
                    onClick={() => removeChecked(tab)}>Delete</button>
            <span className="op-cb muted">Export to</span>
          </div>
          <table className="cd-state-tbl">
            <thead>
              <tr>
                {/* col 0 = select checkbox (+ select-all), col 1 = edit pencil (owner request 2026-10-26, image 987). */}
                <th className="cd-cb">
                  <input type="checkbox" aria-label="Select all"
                         checked={tabRows.length > 0 && tabSel.size === tabRows.length}
                         onChange={() => toggleAll(tab, tabRows.length)} />
                </th>
                <th className="cd-ed" aria-label="Edit" />
                {tabDef.cols.map((c) => <th key={c}>{c}</th>)}
              </tr>
            </thead>
            <tbody>
              {tabRows.length === 0 ? (
                <tr><td colSpan={tabDef.cols.length + 2} className="cd-empty">No data to display</td></tr>
              ) : (
                tabRows.map((r, i) => (
                  <tr key={i} className={tabSel.has(i) ? "sel" : ""}>
                    <td className="cd-cb">
                      <input type="checkbox" aria-label="Select row"
                             checked={tabSel.has(i)} onChange={() => toggleRow(tab, i)} />
                    </td>
                    <td className="cd-ed">
                      <button type="button" className="cd-ed-btn" title="Edit"
                              onClick={() => setChildDlg({ tab, editIdx: i })}>
                        <i className="ti ti-edit" aria-hidden="true" />
                      </button>
                    </td>
                    {tabDef.row(r).map((v, j) => <td key={j}>{v}</td>)}
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <div className="op-count">Count = {tabRows.length}</div>
        </div>
      </div>

      {/* Category "+" (QuickAddDialog) + the Country master popup (CountryDialog). On success each
          re-reads its field's options and selects the new row. */}
      {qaField?.quickAdd && (
        <QuickAddDialog
          spec={qaField.quickAdd}
          optionsUrl={qaField.src ?? ""}
          onClose={() => setQaField(null)}
          onAdded={(options, selectedId) => {
            applyAdded(qaField, options, selectedId);
            setQaField(null);
          }}
        />
      )}
      {countryField && (
        <CountryDialog
          createUrl="/api/v1/system/countries"
          optionsUrl={countryField.src ?? ""}
          onClose={() => setCountryField(null)}
          onAdded={(options, selectedId) => {
            applyAdded(countryField, options, selectedId);
            setCountryField(null);
          }}
        />
      )}
      {/* Source Lead "+" → the full legacy "Open Prospect" form (owner report 2026-10-26, image 984).
          Creates a CRM Prospect via /clients/lookups/source-leads (which /customers/source-leads reads
          back), then re-reads this field's options and selects it. */}
      {prospectField && (
        <OpenProspectDialog
          createUrl="/api/v1/clients/lookups/source-leads"
          optionsUrl={prospectField.src ?? ""}
          onClose={() => setProspectField(null)}
          onAdded={(options, selectedId) => {
            applyAdded(prospectField, options, selectedId);
            setProspectField(null);
          }}
        />
      )}
      {/* Child-tab New/Edit — the SAME child form the Unfollowed Clients tabs use, in BUFFER mode:
          onCapture hands the payload back (the parent client does not exist yet) instead of POSTing,
          and it is written under the client when this dialog's OK creates it. */}
      {childDlg && (() => {
        const def = TAB_DEFS.find((t) => t.key === childDlg.tab)!;
        const editing = childDlg.editIdx != null;
        const prefill = editing ? (buffered[childDlg.tab] ?? [])[childDlg.editIdx!] : undefined;
        return (
          <ModuleCrudDialog
            config={MODULE_CRUD[def.childKey]!}
            id={editing ? `pending-${childDlg.editIdx}` : null}
            mode={editing ? "edit" : undefined}
            fixedValues={def.fixed}
            prefill={prefill}
            onCapture={(payload) => {
              setBuffered((b) => {
                const list = [...(b[childDlg.tab] ?? [])];
                if (editing) list[childDlg.editIdx!] = payload;
                else list.push(payload);
                return { ...b, [childDlg.tab]: list };
              });
              setChildDlg(null);
            }}
            onClose={() => setChildDlg(null)}
            onSaved={() => setChildDlg(null)}
          />
        );
      })()}
    </AppDialog>
  );
}
